import { UnitUniqueName } from '@shared/models/unit';

export interface BerthProductTenantRelationRef {
    id: number;
}

/** No configured relations → always show; otherwise only when the booking relation matches. */
export function matchesBerthProductTenantRelation(
    relations: readonly BerthProductTenantRelationRef[] | null | undefined,
    selectedRelationId: number | null | undefined,
): boolean {
    const configured = relations ?? [];
    if (!configured.length) {
        return true;
    }
    if (selectedRelationId == null) {
        return false;
    }
    return configured.some(relation => relation.id === selectedRelationId);
}

/** Applies stay-duration auto-quantity for day/overnight units unless the berth-product flag opts out. */
export function resolveBerthProductQuantity(
    configuredQuantity: number | null | undefined,
    durationOfStay: number,
    unitUniqueName: UnitUniqueName | string | null | undefined,
    skipStayDurationQuantity: boolean,
): number {
    const base = configuredQuantity ?? 0;
    if (skipStayDurationQuantity) {
        return base;
    }
    if (durationOfStay > 0
        && [UnitUniqueName.OVERNIGHT_STAY, UnitUniqueName.DAY].includes(unitUniqueName as UnitUniqueName)) {
        return durationOfStay;
    }
    return base;
}

export function shouldOmitZeroPriceBerthPosition(
    fromBerth: boolean,
    ignoreFreeOfChargePosition: boolean,
    price: number | string | null | undefined,
    quantity: number | null | undefined,
): boolean {
    return fromBerth
        && !!ignoreFreeOfChargePosition
        && (toNumericPrice(price) * (quantity ?? 0)) === 0;
}

export interface BerthInvoicePositionForSave {
    id?: number;
    fromBerth?: boolean;
    fromTenantRelation?: boolean;
    tenantRelationAssignment?: unknown;
    bcm_berth_has_assignments_id?: number;
    berthAssignmentUuid?: string;
    product?: { ignoreFreeOfChargePosition?: boolean };
    price?: number | string | null;
    quantity?: number | null;
    deleted?: boolean;
}

export function isBerthBookingPosition(position: BerthInvoicePositionForSave): boolean {
    return !!position.fromBerth
        || !!position.bcm_berth_has_assignments_id
        || !!position.berthAssignmentUuid;
}

/** Drops unsaved zero-total berth positions; marks persisted ones as deleted. */
export function filterPositionsForSave<T extends BerthInvoicePositionForSave>(positions: readonly T[]): T[] {
    return positions
        .map(position => {
            if (!shouldOmitZeroPriceBerthPosition(
                isBerthBookingPosition(position),
                !!position.product?.ignoreFreeOfChargePosition,
                position.price,
                position.quantity,
            )) {
                return position;
            }

            if (position.id) {
                return {...position, deleted: true};
            }

            return null;
        })
        .filter((position): position is T => position != null);
}

/** Beziehungspositionen mit Menge 0 bleiben im Dialog, gehen aber nicht auf den Bierdeckel (#7140). */
export function shouldOmitZeroQuantityTenantRelationPosition(
    fromTenantRelation: boolean | undefined,
    quantity: number | null | undefined,
): boolean {
    return !!fromTenantRelation && (quantity ?? 0) <= 0;
}

export function filterTenantRelationPositionsForSave<T extends BerthInvoicePositionForSave>(positions: readonly T[]): T[] {
    return positions
        .map(position => {
            // Saved positions come back without fromTenantRelation, only with their assignment.
            const fromTenantRelation = !!position.fromTenantRelation || !!position.tenantRelationAssignment;

            if (!shouldOmitZeroQuantityTenantRelationPosition(fromTenantRelation, position.quantity)) {
                return position;
            }

            if (position.id) {
                return {...position, deleted: true};
            }

            return null;
        })
        .filter((position): position is T => position != null);
}

function toNumericPrice(price: number | string | null | undefined): number {
    if (price == null || price === '') {
        return 0;
    }

    const numericPrice = typeof price === 'number' ? price : Number(price);

    return Number.isFinite(numericPrice) ? numericPrice : 0;
}

export function isDayOrOvernightProductUnit(product: { unit?: { uniqueName?: UnitUniqueName | string } } | null | undefined): boolean {
    return [UnitUniqueName.DAY, UnitUniqueName.OVERNIGHT_STAY].includes(product?.unit?.uniqueName as UnitUniqueName);
}

export interface BerthProductForBooking {
    id?: number;
    quantity?: number | null;
    skipStayDurationQuantity?: boolean;
    ignoreFreeOfChargePosition?: boolean;
    unit?: { uniqueName?: UnitUniqueName | string };
    forTenantRelations?: BerthProductTenantRelationRef[];
}

export function filterBerthProductsForBooking<T extends BerthProductForBooking>(
    products: readonly T[] | null | undefined,
    tenantRelationId: number | null | undefined,
): T[] {
    return (products || [])
        .filter(product => matchesBerthProductTenantRelation(product.forTenantRelations, tenantRelationId));
}

export function resolveBerthProductQuantityForBooking(
    product: BerthProductForBooking,
    durationOfStay: number,
): number {
    return resolveBerthProductQuantity(
        product.quantity,
        durationOfStay,
        product.unit?.uniqueName,
        !!product.skipStayDurationQuantity,
    );
}

export function isDuplicateBerthBookingPosition(
    existing: BerthInvoicePositionForSave & { product?: { id?: number }; berthAssignmentUuid?: string },
    candidate: BerthInvoicePositionForSave & { product?: { id?: number }; berthAssignmentUuid?: string },
): boolean {
    return !!candidate.fromBerth
        && !!existing.fromBerth
        && !existing.id
        && !candidate.id
        && existing.product?.id === candidate.product?.id
        && existing.berthAssignmentUuid === candidate.berthAssignmentUuid;
}
